import React, { useState, useEffect, useRef } from 'react';
import { fetchTimeZone } from '../../Utilities/api';
import { CREDIT_COSTS } from '../../Utilities/creditCosts';
import useEntitlementsStore from '../../Utilities/entitlementsStore';
import GooglePlaceAutocomplete from '../shared/GooglePlaceAutocomplete';
import './AddChartModal.css';

function AddChartModal({ isOpen, onClose, userId, onSubmit }) {
  const isPlus = useEntitlementsStore((state) =>
    (state.plan === 'PLUS' || state.plan === 'PREMIUM') && state.isSubscriptionActive
  );
  const isSimple = useEntitlementsStore((state) => state.pricingModel === 'simple');
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [unknownTime, setUnknownTime] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [locationInputKey, setLocationInputKey] = useState(0);
  const photoInputRef = useRef(null);

  // UI state
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFirstName('');
      setLastName('');
      setGender('');
      setPlaceOfBirth('');
      setLat('');
      setLon('');
      setDate('');
      setTime('');
      setUnknownTime(false);
      setFormErrors({});
      setSubmitError('');
      setPhotoFile(null);
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
      setPhotoPreview(null);
      setLocationInputKey((current) => current + 1);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      setFormErrors(prev => ({ ...prev, photo: 'Image must be under 5MB' }));
      return;
    }

    setFormErrors(prev => { const { photo, ...rest } = prev; return rest; });
    setPhotoFile(file);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation();
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!gender) errors.gender = "Gender is required";
    if (!lat || !lon) errors.location = "Birth location is required";
    if (!date) errors.date = "Birth date is required";
    if (!unknownTime && !time) errors.time = "Birth time is required";
    return errors;
  };

  const handlePlaceSelect = ({ formattedAddress, lat, lon }) => {
    try {
      if (lat == null || lon == null) {
        console.error('Invalid place object or missing coordinates:', { formattedAddress, lat, lon });
        return;
      }
      setLat(lat);
      setLon(lon);
      setPlaceOfBirth(formattedAddress);
      setFormErrors((prev) => {
        const { location, ...rest } = prev;
        return rest;
      });
    } catch (error) {
      console.error('Error processing place selection:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setSubmitError('');
    setIsSubmitting(true);

    try {
      // Calculate timezone offset
      const timeForTimezone = unknownTime ? '12:00' : time;
      const dateTimeString = `${date}T${timeForTimezone}:00`;
      const dateTime = new Date(dateTimeString);
      const epochTimeSeconds = Math.floor(dateTime.getTime() / 1000);
      const totalOffsetHours = await fetchTimeZone(lat, lon, epochTimeSeconds);

      // Prepare guest data
      const guestData = {
        ownerUserId: userId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: date,
        placeOfBirth,
        time: unknownTime ? 'unknown' : time,
        lat: parseFloat(lat),
        lon: parseFloat(lon),
        tzone: parseFloat(totalOffsetHours),
        gender,
        unknownTime,
        ...(photoFile && { photoFile })
      };

      // Close modal immediately and let parent handle the API call
      onClose();
      onSubmit(guestData);
    } catch (error) {
      console.error('Error preparing guest data:', error);
      setSubmitError(error.message || 'Failed to prepare chart data. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="add-chart-modal__content">
        <div className="add-chart-modal__header">
          <div className="add-chart-modal__header-text">
            <h2>Add Birth Chart</h2>
            <p className="add-chart-modal__subtitle">
              {isSimple
                ? 'Free — includes a chart overview'
                : isPlus
                  ? 'Included with Plus'
                  : `Costs ${CREDIT_COSTS.GUEST_CHART} credit`}
            </p>
          </div>
          <button
            className="add-chart-modal__close-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-chart-modal__form">
          {/* Optional Photo */}
          <div className="modal-photo-upload">
            <div
              className="modal-photo-upload__circle"
              onClick={() => !isSubmitting && photoInputRef.current?.click()}
            >
              {photoPreview ? (
                <>
                  <img src={photoPreview} alt="Preview" className="modal-photo-upload__img" />
                  <button
                    type="button"
                    className="modal-photo-upload__remove"
                    onClick={handleRemovePhoto}
                    disabled={isSubmitting}
                  >
                    &times;
                  </button>
                </>
              ) : (
                <div className="modal-photo-upload__placeholder">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
                  </svg>
                </div>
              )}
            </div>
            <span className="modal-photo-upload__hint">Add photo (optional)</span>
            {formErrors.photo && (
              <span className="add-chart-modal__field-error">{formErrors.photo}</span>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* Name fields */}
          <div className="add-chart-modal__row">
            <div className="add-chart-modal__group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                disabled={isSubmitting}
                className={formErrors.firstName ? 'error' : ''}
              />
              {formErrors.firstName && (
                <span className="add-chart-modal__field-error">{formErrors.firstName}</span>
              )}
            </div>
            <div className="add-chart-modal__group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name (optional)"
                disabled={isSubmitting}
                className={formErrors.lastName ? 'error' : ''}
              />
              {formErrors.lastName && (
                <span className="add-chart-modal__field-error">{formErrors.lastName}</span>
              )}
            </div>
          </div>

          {/* Gender */}
          <div className="add-chart-modal__group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              disabled={isSubmitting}
              className={formErrors.gender ? 'error' : ''}
            >
              <option value="">Select gender...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="nonbinary">Non-binary</option>
            </select>
            {formErrors.gender && (
              <span className="add-chart-modal__field-error">{formErrors.gender}</span>
            )}
          </div>

          {/* Birth Location */}
          <div className="add-chart-modal__group">
            <label htmlFor="location">Birth Location</label>
            <GooglePlaceAutocomplete
              key={locationInputKey}
              onPlaceSelected={handlePlaceSelect}
              placeholder="City, Country"
              disabled={isSubmitting}
              className={`add-chart-modal__location-input ${formErrors.location ? 'error' : ''}`}
            />
            {formErrors.location && (
              <span className="add-chart-modal__field-error">{formErrors.location}</span>
            )}
          </div>

          {/* Birth Date */}
          <div className="add-chart-modal__group">
            <label htmlFor="date">Birth Date</label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={isSubmitting}
              className={formErrors.date ? 'error' : ''}
            />
            {formErrors.date && (
              <span className="add-chart-modal__field-error">{formErrors.date}</span>
            )}
          </div>

          {/* Birth Time */}
          <div className="add-chart-modal__group">
            <label>Birth Time</label>
            <div className="add-chart-modal__time-row">
              <select
                value={unknownTime ? 'unknown' : 'known'}
                onChange={(e) => {
                  if (e.target.value === 'unknown') {
                    setUnknownTime(true);
                    setTime('');
                  } else {
                    setUnknownTime(false);
                  }
                }}
                disabled={isSubmitting}
                className="add-chart-modal__time-select"
              >
                <option value="known">Known Time</option>
                <option value="unknown">Unknown</option>
              </select>
              {!unknownTime && (
                <input
                  type="time"
                  id="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  disabled={isSubmitting}
                  className={`add-chart-modal__time-input ${formErrors.time ? 'error' : ''}`}
                />
              )}
            </div>
            {formErrors.time && (
              <span className="add-chart-modal__field-error">{formErrors.time}</span>
            )}
          </div>

          {/* Submit Error */}
          {submitError && (
            <div className="add-chart-modal__submit-error">{submitError}</div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="add-chart-modal__submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Chart...' : 'Create Birth Chart'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddChartModal;
